import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/Button/Button';
import { Input } from '../../components/Input/Input';
import { OverlaySpinner } from '../../components/OverlaySpinner/OverlaySpinner';
import { Select } from '../../components/Select/Select';
import { ValidationErrors, RebookStatus } from '../../types';
import Swal from 'sweetalert2';
import styles from './PublicDispute.module.scss';
import * as api from '../../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const DISPUTE_REASONS = [
  { label: 'Measurement provided were accurate', value: 'measurement_accurate' },
  { label: 'Provided building type is correct', value: 'building_type_correct' },
  { label: 'Redelivery did not occur', value: 'redelivery_did_not_occur' },
  { label: 'Dispute futile collection attempt', value: 'dispute_collection_attempt' },
  { label: 'My shipment was appropriately packaged', value: 'shipment_appropriately_packaged' },
  { label: 'Other', value: 'other' },
];

const FILE_CONFIG = {
  VALID_TYPES: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  MAX_PER_FILE: 5 * 1024 * 1024,      // 5 MB
  MAX_TOTAL: 10 * 1024 * 1024,        // 10 MB
  MAX_FILES: 5,
};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type Props = {
  className?: string;
};

type DisputeData = {
  dispute_id: string;
  adjustment_invoice_id?: number;
  order_hash_id: string;
  amount: number;
  reason?: string;
  courier_name: string;
  pickup_address?: string;
  destination_address?: string;
  customer_email: string;
  pickup_building_type?: string;
  destination_building_type?: string;
};

type DisputeStatusData = {
  dispute_id: string;
  status: string;
  message: string;
  status_description: string;
  dispute_reason?: string;
  dispute_files?: boolean;
  order_hash_id?: string;
  rejection_reason?: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// SVG Components
// ─────────────────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="10" fill="#fee2e2"/>
    <path d="M10 6v5M10 13v1" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const AlertIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="24" fill="#fee2e2"/>
    <path d="M24 14v14M24 32v2" stroke="#dc2626" strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// Helper Components
// ─────────────────────────────────────────────────────────────────────────────

interface DetailItemProps {
  label: string;
  value: React.ReactNode;
  action?: React.ReactNode;
  inline?: boolean;
}

const DetailItem: React.FC<DetailItemProps> = ({ label, value, action, inline = false }) => (
  <div className={`${styles.detailItem} ${inline ? styles.detailItemInline : ''}`}>
    {inline ? (
      <>
        <span className={styles.detailLabelInline}>{label}: </span>
        <span className={styles.detailValueInline}>{value}</span>
      </>
    ) : (
      <>
        <span className={styles.detailLabel}>{label}</span>
        <span className={styles.detailValue}>{value}</span>
      </>
    )}
    {action && <div style={{ marginTop: '6px' }}>{action}</div>}
  </div>
);

interface ReferenceChipProps {
  label: string;
  value: string;
}

const ReferenceChip: React.FC<ReferenceChipProps> = ({ label, value }) => (
  <div className={styles.refChip}>
    <span className={styles.refChipLabel}>{label}</span>
    <span className={styles.refChipDot} />
    <code className={styles.refChipValue}>{value}</code>
  </div>
);

interface StatusBadgeProps {
  status: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const displayStatus = status === 'accepted_loss' ? 'Accepted' : 
    (typeof status === 'string' && status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown');

  return (
    <span className={`${styles.statusPill} ${styles[`statusPill_${status}`]}`}>
      {displayStatus}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// State & Views
// ─────────────────────────────────────────────────────────────────────────────

export function PublicDispute(props: Props) {
  const { disputeId: routeDisputeId } = useParams<{ disputeId?: string }>();

  // Form state
  const [email, setEmail] = useState<string>('');
  const [disputeReason, setDisputeReason] = useState<string>('measurement_accurate');
  const [description, setDescription] = useState<string>('');
  const [amountDisputed, setAmountDisputed] = useState<string>('');
  const [additionalInfo, setAdditionalInfo] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);

  // UI state
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [status, setStatus] = useState<RebookStatus>('idle');
  const [apiMessage, setApiMessage] = useState<string>('');
  const [apiMessageType, setApiMessageType] = useState<'success' | 'error' | ''>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  // Data state
  const [disputeData, setDisputeData] = useState<DisputeData | null>(null);
  const [disputeStatusData, setDisputeStatusData] = useState<DisputeStatusData | null>(null);
  const [submittedDisputeId, setSubmittedDisputeId] = useState<string>('');
  const [submittedDisputeReason, setSubmittedDisputeReason] = useState<string>('');
  const [submittedDisputeFiles, setSubmittedDisputeFiles] = useState<boolean>(false);

  // ───────────────────────────────────────────────────────────────────────────
  // Effects
  // ───────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    document.title = 'Dispute - Fast Courier';
    fetchDisputeData();
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // API Calls
  // ───────────────────────────────────────────────────────────────────────────

  const fetchDisputeData = async () => {
    if (!routeDisputeId) {
      setErrorMessage('Invalid dispute link. No dispute ID provided.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get<any>(`/public/dispute/${routeDisputeId}`);

      if (!response) {
        setErrorMessage('Failed to load dispute data.');
        return;
      }

      if (response.status) {
        if (response.dispute_id) {
          setDisputeStatusData({
            dispute_id: response.dispute_id,
            status: response.dispute_status ?? '',
            message: response.message,
            status_description: response.status_description,
            dispute_reason: response.dispute_reason,
            dispute_files: response.dispute_files,
            order_hash_id: response.order_hash_id,
            rejection_reason: response.rejection_reason,
          });
        } else {
          setDisputeData(response.data);
          if (response.data.customer_email) {
            setEmail(response.data.customer_email);
          }
        }
      } else {
        setErrorMessage(response.message || 'Failed to load dispute data.');
      }
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Failed to load dispute data. Please try again later.';
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadDocument = async (disputeId: string) => {
    try {
      const response = await api.get<any>(`/public/dispute/${disputeId}/files`);
      if (response.status && response.data && response.data.length > 0) {
        response.data.forEach((file: any, idx: number) => {
          setTimeout(() => {
            const win = window.open(file.attachment_url, '_blank');
            if (!win || win.closed) {
              window.location.href = file.attachment_url;
            }
          }, 200 * idx);
        });
      } else {
        Swal.fire({
          title: 'No Files',
          text: 'No files available for download.',
          icon: 'info',
          buttonsStyling: false,
          confirmButtonText: 'Ok',
          customClass: { confirmButton: 'btn btn-primary' }
        });
      }
    } catch (e: any) {
      Swal.fire({
        title: 'Error',
        text: 'Failed to download files. Please try again.',
        icon: 'error',
        buttonsStyling: false,
        confirmButtonText: 'Ok',
        customClass: { confirmButton: 'btn btn-primary' }
      });
    }
  };

  const submitDispute = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!routeDisputeId) {
      setApiMessage('Invalid dispute ID.');
      return;
    }

    // Validate form
    const newErrors: ValidationErrors = {};
    if (!email) {
      newErrors.email = ['Email is required'];
    } else if (!validateEmail(email)) {
      newErrors.email = ['Please enter a valid email address'];
    }
    if (!disputeReason) newErrors.disputeReason = ['Dispute reason is required'];
    if (!description) newErrors.description = ['Description is required'];
    if (!amountDisputed) newErrors.amountDisputed = ['Amount disputed is required'];

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      scrollToFirstError(Object.keys(newErrors)[0]);
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStatus('in-progress');

    try {
      const formData = new FormData();
      if (routeDisputeId) {
        formData.append('disputeId', routeDisputeId);
      }
      formData.append('email', email);
      formData.append('disputeReason', disputeReason);
      formData.append('description', description);
      formData.append('amountDisputed', amountDisputed);
      formData.append('additionalInfo', additionalInfo);
      files.forEach((file) => {
        formData.append('files[]', file);
      });

      const response = await api.post<any>(`/public/dispute`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.status) {
        resetForm();
        setApiMessageType('success');
        setIsSubmitted(true);
        setSubmittedDisputeId(response?.data?.dispute_id || '');
        setSubmittedDisputeReason(response?.data?.dispute_reason || '');
        setSubmittedDisputeFiles(response?.data?.dispute_files ?? false);
      } else {
        setApiMessageType('error');
        setIsSubmitted(false);
      }
      setApiMessage(response.message);
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Something went wrong!';
      setApiMessage(message);
      setApiMessageType('error');
      setIsSubmitted(false);
    } finally {
      setStatus('idle');
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Handlers
  // ───────────────────────────────────────────────────────────────────────────

  const handleEmailChange = (value: string) => {
    setEmail(value);
    // Clear email error when user starts typing
    if (errors.email) {
      setErrors(prev => {
        const { email: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selectedFiles = Array.from(e.target.files);
    const errorMessages = validateFiles(selectedFiles);

    if (errorMessages) {
      setErrors(prev => ({ ...prev, files: [errorMessages] }));
      return;
    }

    setFiles(selectedFiles);
    setErrors(prev => { const { files: _, ...rest } = prev; return rest; });
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Validators & Utilities
  // ───────────────────────────────────────────────────────────────────────────

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateFiles = (selectedFiles: File[]): string | null => {
    if (selectedFiles.length > FILE_CONFIG.MAX_FILES) {
      return `Maximum ${FILE_CONFIG.MAX_FILES} files allowed`;
    }

    const invalid = selectedFiles.find(f => !FILE_CONFIG.VALID_TYPES.includes(f.type));
    if (invalid) {
      return `Invalid file type: ${invalid.name}`;
    }

    const oversized = selectedFiles.find(f => f.size > FILE_CONFIG.MAX_PER_FILE);
    if (oversized) {
      return `${oversized.name} exceeds 5 MB`;
    }

    const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > FILE_CONFIG.MAX_TOTAL) {
      return 'Total size must not exceed 10 MB';
    }

    return null;
  };

  const scrollToFirstError = (fieldName: string) => {
    const selector = fieldName === 'description' 
      ? `textarea[name="${fieldName}"]` 
      : `[name="${fieldName}"]`;
    const element = document.querySelector(selector) as HTMLElement;
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus();
    }
  };

  const resetForm = () => {
    setEmail(disputeData?.customer_email || '');
    setDisputeReason('measurement_accurate');
    setDescription('');
    setAmountDisputed('');
    setAdditionalInfo('');
    setFiles([]);
    setErrors({});
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Render Helpers
  // ───────────────────────────────────────────────────────────────────────────

  const isStatusAccepted = (status: string) => status === 'accepted' || status === 'accepted_loss';
  const isStatusRejected = (status: string) => status === 'rejected';

  // ───────────────────────────────────────────────────────────────────────────
  // Render Views
  // ───────────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className={styles.root} style={{ minHeight: '25vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <OverlaySpinner enabled={true} />
      </div>
    );
  }

  // View: Dispute Status (already submitted/decided)
  if (disputeStatusData) {
    return <DisputeStatusView data={disputeStatusData} onDownload={downloadDocument} />;
  }

  // View: Success (just submitted)
  if (isSubmitted) {
    return (
      <SuccessView
        submittedDisputeId={submittedDisputeId}
        submittedDisputeReason={submittedDisputeReason}
        submittedDisputeFiles={submittedDisputeFiles}
        amountDisputed={amountDisputed}
        orderHashId={disputeData?.order_hash_id}
        filesCount={files.length}
        onDownload={downloadDocument}
      />
    );
  }

  // View: Error (invalid link)
  if (errorMessage) {
    return <ErrorView message={errorMessage} />;
  }

  // View: Main form
  return (
    <FormView
      disputeData={disputeData}
      apiMessage={apiMessage}
      apiMessageType={apiMessageType}
      email={email}
      onEmailChange={handleEmailChange}
      disputeReason={disputeReason}
      onDisputeReasonChange={setDisputeReason}
      description={description}
      onDescriptionChange={setDescription}
      amountDisputed={amountDisputed}
      onAmountDisputedChange={setAmountDisputed}
      additionalInfo={additionalInfo}
      onAdditionalInfoChange={setAdditionalInfo}
      files={files}
      onFilesChange={handleFileChange}
      errors={errors}
      isSubmitting={status === 'in-progress'}
      onSubmit={submitDispute}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// View Components
// ─────────────────────────────────────────────────────────────────────────────

interface DisputeStatusViewProps {
  data: DisputeStatusData;
  onDownload: (disputeId: string) => Promise<void>;
}

const DisputeStatusView: React.FC<DisputeStatusViewProps> = ({ data, onDownload }) => {
  const isAccepted = data.status === 'accepted' || data.status === 'accepted_loss';
  const isRejected = data.status === 'rejected';
  const isOpen = !isAccepted && !isRejected;

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={`${styles.statusCard} ${styles[`statusCard_${data.status}`]}`}>
          <div className={styles.statusCardInner}>
            <div className={styles.statusCardBody}>
              <div className={styles.statusCardHeader}>
                <h2 className={styles.statusCardTitle}>{data.message}</h2>
                <StatusBadge status={data.status} />
              </div>

              <p className={styles.statusCardDesc}>{data.status_description}</p>

              {isRejected && data.rejection_reason && (
                <p className={styles.statusCardDesc}>
                  <strong>Rejection Reason:</strong> {data.rejection_reason}
                </p>
              )}

              <div className={styles.statusCardMeta}>
                <span className={styles.statusCardLabel}>Dispute Reference:</span>
                <code className={styles.statusCardId}>{data.dispute_id}</code>
              </div>

              {data.order_hash_id && (
                <div className={styles.statusCardMeta} style={{ marginLeft: '8px' }}>
                  <span className={styles.statusCardLabel}>Order ID:</span>
                  <code className={styles.statusCardId}>{data.order_hash_id}</code>
                </div>
              )}

              {(data.dispute_reason || data.dispute_files) && (
                <div className={styles.statusDetailsBox}>
                  <div className={styles.statusDetailsRow}>
                    {data.dispute_reason && (
                      <div className={styles.statusDetailItem} style={{ flex: 1 }}>
                        <span className={styles.statusDetailLabel}>Dispute Reason:</span>
                        <span className={styles.statusDetailValue}>{data.dispute_reason}</span>
                      </div>
                    )}
                    {data.dispute_files && (
                      <div className={styles.statusDetailItem} style={{ flex: 1, marginLeft: data.dispute_reason ? '16px' : '0' }}>
                        <span className={styles.statusDetailLabel}>Supporting Documents:</span>
                        <button
                          className={styles.downloadButtonSmall}
                          onClick={() => onDownload(data.dispute_id)}
                        >
                          <DownloadIcon />
                          Download Files
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={`${styles.statusCardFooter} ${styles[`statusCardFooter_${data.status}`]}`}>
            {isOpen && <span>⏱ Our team is reviewing your dispute. You'll be notified by email.</span>}
            {isAccepted && <span>✓ If you have questions, please contact our <a href={`${process.env.REACT_APP_HOME_URL}/contact`} target="_blank" rel="noopener noreferrer">support team</a>.</span>}
            {isRejected && <span>If you have questions, please contact our <a href={`${process.env.REACT_APP_HOME_URL}/contact`} target="_blank" rel="noopener noreferrer">support team</a>.</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

interface SuccessViewProps {
  submittedDisputeId: string;
  submittedDisputeReason: string;
  submittedDisputeFiles: boolean;
  amountDisputed: string;
  orderHashId?: string;
  filesCount: number;
  onDownload: (disputeId: string) => Promise<void>;
}

const SuccessView: React.FC<SuccessViewProps> = ({
  submittedDisputeId,
  submittedDisputeReason,
  submittedDisputeFiles,
  amountDisputed,
  orderHashId,
  filesCount,
  onDownload,
}) => {
  const submittedAt = new Date().toLocaleString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.successCard}>
          <div className={styles.successCardTop}>
            <div className={styles.successIconWrap}>
              <CheckIcon />
            </div>
            <h2 className={styles.successCardTitle}>Dispute submitted successfully</h2>
            <p className={styles.successCardSub}>
              Your dispute has been received and is now under review.
            </p>

            <div className={styles.refRow}>
              {submittedDisputeId && <ReferenceChip label="Dispute ID" value={submittedDisputeId} />}
              {orderHashId && <ReferenceChip label="Order ID" value={orderHashId} />}
            </div>
          </div>

          <div className={styles.successCardBody}>
            <div className={styles.detailRow}>
              {submittedDisputeReason && (
                <DetailItem label="Dispute reason" value={submittedDisputeReason} inline />
              )}
              {amountDisputed && (
                <DetailItem label="Amount disputed" value={`$${amountDisputed}`} />
              )}
            </div>

            {submittedDisputeFiles && (
              <div className={styles.detailRow}>
                <DetailItem
                  label="Supporting documents"
                  value={`${filesCount} file${filesCount !== 1 ? 's' : ''} attached`}
                  action={
                    <button
                      className={styles.downloadButton}
                      onClick={() => onDownload(submittedDisputeId)}
                    >
                      <DownloadIcon />
                      Download files
                    </button>
                  }
                />
              </div>
            )}
          </div>

          <div className={styles.successCardFooter}>
            <div className={styles.noticeBox}>
              <InfoIcon />
              <p>We'll review your dispute and notify you once a decision has been made.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ErrorViewProps {
  message: string;
}

const ErrorView: React.FC<ErrorViewProps> = ({ message }) => (
  <div className={styles.root}>
    <div className={styles.container}>
      <div className={styles.errorCard}>
        <div className={styles.errorCardIcon}>
          <AlertIcon />
        </div>
        <h2 className={styles.errorCardTitle}>Unable to Load Dispute</h2>
        <p className={styles.errorCardMessage}>{message}</p>
      </div>
    </div>
  </div>
);

interface FormViewProps {
  disputeData: DisputeData | null;
  apiMessage: string;
  apiMessageType: 'success' | 'error' | '';
  email: string;
  onEmailChange: (email: string) => void;
  disputeReason: string;
  onDisputeReasonChange: (reason: string) => void;
  description: string;
  onDescriptionChange: (description: string) => void;
  amountDisputed: string;
  onAmountDisputedChange: (amount: string) => void;
  additionalInfo: string;
  onAdditionalInfoChange: (info: string) => void;
  files: File[];
  onFilesChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errors: ValidationErrors;
  isSubmitting: boolean;
  onSubmit: (e: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
}

const FormView: React.FC<FormViewProps> = ({
  disputeData,
  apiMessage,
  apiMessageType,
  email,
  onEmailChange,
  disputeReason,
  onDisputeReasonChange,
  description,
  onDescriptionChange,
  amountDisputed,
  onAmountDisputedChange,
  additionalInfo,
  onAdditionalInfoChange,
  files,
  onFilesChange,
  errors,
  isSubmitting,
  onSubmit,
}) => {
  return (
    <div className={styles.root}>
      <OverlaySpinner enabled={isSubmitting} />

      <div className={styles.container}>
        <h1 className={styles.title}>Raise a Dispute</h1>

        {disputeData && (
          <div className={styles.disputeInfo}>
            <p><strong>Order ID:</strong> {disputeData.order_hash_id}</p>
          </div>
        )}

        {apiMessage && apiMessageType === 'error' && (
          <div className={styles.alertBanner}>
            <ErrorIcon />
            <span>{apiMessage}</span>
          </div>
        )}

        <form className={styles.form}>
          <div className={styles.row}>
            <div className={styles.column}>
              <label className={styles.label}>
                Preferred Contact Email <span className={styles.required}>*</span>
              </label>
              <Input
                className={styles.input}
                name="email"
                value={email}
                onChange={onEmailChange}
                errors={errors}
                required
                type="text"
              />
            </div>
            <div className={styles.column}>
              <label className={styles.label}>
                Dispute Reason <span className={styles.required}>*</span>
              </label>
              <Select<string>
                className={styles.input}
                onChange={onDisputeReasonChange}
                options={DISPUTE_REASONS}
                value={disputeReason}
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.column}>
              <label className={styles.label}>
                Description <span className={styles.required}>*</span>
              </label>
              <textarea
                className={styles.textarea}
                name="description"
                value={description}
                onChange={(e) => onDescriptionChange(e.target.value)}
                placeholder="Start writing..."
                rows={6}
              />
              {errors.description && (
                <div className={styles.fieldError}>{errors.description[0]}</div>
              )}
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.column}>
              <label className={styles.label}>
                Amount disputed <span className={styles.required}>*</span>
              </label>
              <Input
                className={styles.input}
                name="amountDisputed"
                value={amountDisputed}
                onChange={onAmountDisputedChange}
                errors={errors}
                required
                type="number"
              />
            </div>
            <div className={styles.column}>
              <label className={styles.label}>Upload Image/Supporting document</label>
              <FileUploadInput
                files={files}
                onChange={onFilesChange}
                errors={errors}
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.column}>
              <label className={styles.label}>Additional Info (Optional)</label>
              <textarea
                className={styles.textarea}
                name="additionalInfo"
                value={additionalInfo}
                onChange={(e) => onAdditionalInfoChange(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <div className={styles.toolbar}>
            <Button type="primary" onClick={onSubmit}>
              Submit
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface FileUploadInputProps {
  files: File[];
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errors: ValidationErrors;
}

const FileUploadInput: React.FC<FileUploadInputProps> = ({ files, onChange, errors }) => (
  <>
    <div className={styles.fileUpload}>
      <input
        type="file"
        id="file-upload"
        className={styles.fileInput}
        onChange={onChange}
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
      />
      <label htmlFor="file-upload" className={styles.fileLabel}>
        <span className={styles.fileButton}>Choose files</span>
        <span className={styles.fileText}>
          {files.length > 0 ? `${files.length} file(s) selected` : 'No file chosen'}
        </span>
      </label>
    </div>
    <div className={styles.fileInfo}>
      <p>Allowed formats: PDF, JPG, JPEG, PNG, DOC, DOCX (Max 5 files, 5MB per file, 10MB total)</p>
      <p className={styles.fileNote}>To help us resolve this dispute, we kindly request that you provide photos of the package showing the weight & measurements.</p>
      <p className={styles.fileNote}>Please note the following requirements:</p>
      <ul className={styles.fileRequirements}>
        <li>Provide clear photos with a tape measure visible for each dimension (Length, Width, and Height)</li>
        <li>Include a photo of the item with a clear view of the attached consignment label. (The carrier will not process your claim without this.)</li>
        <li>Photos of printed box dimensions will not be accepted by the couriers.</li>
        <li>eCommerce screenshots or product descriptions will also be rejected.</li>
      </ul>
    </div>
    {errors.files && <div className={styles.fieldError}>{errors.files[0]}</div>}
  </>
);